import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

type RouteAccessPayload = {
  title?: string;
  message?: string;
};

const parseRoutePayload = (error: unknown): RouteAccessPayload => {
  if (!isRouteErrorResponse(error)) return {};
  if (typeof error.data === "string") {
    try {
      return JSON.parse(error.data);
    } catch {
      return {};
    }
  }
  return error.data || {};
};

export const RouteAccessError = () => {
  const navigate = useNavigate();
  const error = useRouteError();
  const payload = parseRoutePayload(error);
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const isFeatureLocked = status === 402;
  const title =
    payload.title ||
    (isFeatureLocked ? "Feature not available" : "Access denied");
  const message =
    payload.message ||
    (isFeatureLocked
      ? "This feature is not enabled for this restaurant's current plan."
      : "You do not have permission to access this page.");

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 80px)",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 5,
        bgcolor: "#120e0d",
        background:
          "radial-gradient(circle at top left, rgba(131, 60, 65, 0.18), transparent 34%), #120e0d",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "min(560px, 100%)",
          borderRadius: "22px",
          p: { xs: 3, md: 4 },
          bgcolor: "rgba(31, 22, 21, 0.72)",
          border: "1px solid rgba(131, 60, 65, 0.24)",
          boxShadow: "0 24px 70px rgba(18, 14, 13, 0.42)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 58,
            height: 58,
            mx: "auto",
            mb: 2,
            borderRadius: "18px",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(131, 60, 65, 0.18)",
            color: "#f0ece8",
          }}
        >
          {isFeatureLocked ? (
            <WorkspacePremiumOutlinedIcon />
          ) : (
            <LockOutlinedIcon />
          )}
        </Box>
        <Typography
          sx={{
            color: "#f0ece8",
            fontFamily: '"Cormorant Infant", Georgia, serif',
            fontSize: { xs: 30, md: 38 },
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: "#a9958e", fontSize: 14, mt: 1.5 }}>
          {message}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="center"
          spacing={1.25}
          sx={{ mt: 3 }}
        >
          <Button
            variant="contained"
            onClick={() => navigate("/app/dashboard")}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#833c41",
              boxShadow: "none",
              "&:hover": { bgcolor: "#9e4a50", boxShadow: "none" },
            }}
          >
            Go to dashboard
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              color: "#f0ece8",
              borderColor: "rgba(240, 236, 232, 0.28)",
              "&:hover": {
                borderColor: "#f0ece8",
                bgcolor: "rgba(240, 236, 232, 0.06)",
              },
            }}
          >
            Go back
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
